import type {
  AnswerResponse,
  CitationResponse,
  ConversationCitationResponse,
  ConversationMessageResponse,
} from './api'

/**
 * One transcript entry: a user query paired with whatever the answer is in
 * right now. `history` turns come from the stored-conversation endpoint and
 * carry the persisted text plus the persisted citation subset; live turns
 * carry the full streaming/ready/error shape.
 */
export type Turn = { id: string; query: string } & (
  | { status: 'streaming'; partialText: string; statusText: string }
  | { status: 'ready'; result: AnswerResponse }
  | {
      status: 'history'
      answerText: string | null
      interactionId: string | null
      citations: CitationResponse[]
    }
  | { status: 'error'; message: string; httpStatus: number | null }
)

/**
 * Widen a persisted citation (interaction_citations stores only number, name,
 * url, source type, scraping source) to the CitationResponse shape CitationList
 * renders. The missing fields get the same neutral defaults the backend uses
 * when it hydrates this table; empty modifiedAt means the staleness check
 * stays off for history turns.
 */
function toDisplayCitation(citation: ConversationCitationResponse): CitationResponse {
  return {
    passageId: '',
    documentName: citation.documentName,
    relevanceScore: 0,
    citationText: `[${citation.documentNumber}]`,
    documentNumber: citation.documentNumber,
    docUrl: citation.docUrl,
    sourceType: citation.sourceType,
    passageText: '',
    scrapingSource: citation.scrapingSource,
    modifiedAt: '',
  }
}

/**
 * Pair stored messages (ascending created_at) into turns. Transcripts are not
 * guaranteed to alternate strictly: a stream that died mid-generation leaves a
 * user message with no assistant reply (answerText: null), and an orphan
 * assistant message gets an empty query.
 */
export function messagesToTurns(messages: ConversationMessageResponse[]): Turn[] {
  const turns: Turn[] = []
  let pendingUser: ConversationMessageResponse | null = null

  const flushPendingUser = () => {
    if (pendingUser === null) return
    turns.push({
      id: `h_${pendingUser.id}`,
      query: pendingUser.content,
      status: 'history',
      answerText: null,
      interactionId: null,
      citations: [],
    })
    pendingUser = null
  }

  for (const message of messages) {
    if (message.role === 'user') {
      flushPendingUser()
      pendingUser = message
    } else {
      turns.push({
        id: `h_${message.id}`,
        query: pendingUser?.content ?? '',
        status: 'history',
        answerText: message.content,
        interactionId: message.interactionId,
        citations: message.citations.map(toDisplayCitation),
      })
      pendingUser = null
    }
  }
  flushPendingUser()
  return turns
}
